import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';

type CanvasCollapse = {
  /** False while suggestions are pending — cards force-expand so nothing hides. */
  collapsible: boolean;
  isExpanded: (id: string) => boolean;
  toggle: (id: string) => void;
  allExpanded: boolean;
  toggleAll: () => void;
};

const CanvasCollapseContext = createContext<CanvasCollapse | null>(null);

type ProviderProps = {
  /** All collapsible node ids, in order — drives expand/collapse-all. */
  nodeIds: string[];
  /** Nodes that must stay open (validation errors, the just-added node) — expanded additively. */
  keepExpanded: string[];
  /** Nodes that start expanded (input schema, rules) but the user can still collapse. */
  defaultExpanded: string[];
  /** When set, every card renders expanded and chevrons hide (e.g. a Percy turn left pending changes). */
  forceExpanded: boolean;
  children: ReactNode;
};

export const CanvasCollapseProvider = ({
  nodeIds,
  keepExpanded,
  defaultExpanded,
  forceExpanded,
  children,
}: ProviderProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(defaultExpanded));

  // Open each default-expanded node the first time it appears (input schema, or a rules
  // card Percy adds later). Tracked in a ref so re-opening happens once per id — a node the
  // user manually collapsed stays collapsed.
  const seededRef = useRef<Set<string>>(new Set(defaultExpanded));
  const defaultKey = defaultExpanded.join('\n');
  useEffect(() => {
    const fresh = defaultExpanded.filter(id => !seededRef.current.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) seededRef.current.add(id);
    setExpanded(prev => new Set([...prev, ...fresh]));
  }, [defaultKey]);

  // Additively pop open any node that gains an error or was just added; never
  // collapses, so a user's manual collapse of a different card survives.
  const keepKey = keepExpanded.join('\n');
  useEffect(() => {
    if (keepExpanded.length === 0) return;
    setExpanded(prev => {
      if (keepExpanded.every(id => prev.has(id))) return prev;
      return new Set([...prev, ...keepExpanded]);
    });
  }, [keepKey]);

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allExpanded = nodeIds.length > 0 && nodeIds.every(id => expanded.has(id));
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(nodeIds));

  const value: CanvasCollapse = {
    collapsible: !forceExpanded,
    isExpanded: id => forceExpanded || expanded.has(id),
    toggle,
    allExpanded,
    toggleAll,
  };

  return <CanvasCollapseContext.Provider value={value}>{children}</CanvasCollapseContext.Provider>;
};

/** Null outside a provider — blocks rendered elsewhere stay fully expanded. */
export const useCanvasCollapse = (): CanvasCollapse | null => useContext(CanvasCollapseContext);

type NodeCollapse = {
  collapsible: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  collapseAriaLabel: string;
};

/** Per-card collapse wiring — returns the collapse-related `PlanBlock` props for a node. */
export const useNodeCollapse = (id: string, expandLabel: string, collapseLabel: string): NodeCollapse => {
  const collapse = useCanvasCollapse();
  const collapsible = collapse?.collapsible ?? false;
  const collapsed = collapsible && !collapse?.isExpanded(id);
  return {
    collapsible,
    collapsed,
    onToggleCollapsed: () => collapse?.toggle(id),
    collapseAriaLabel: collapsed ? expandLabel : collapseLabel,
  };
};
